import { useCallback, useMemo } from 'react';
import createContext from 'constate';
import {
  formatWithCurrency,
  formatWithCurrencyWithoutDecimals,
  formatWithoutCurrencyAndDecimals,
} from '../utils/format';
import { useQueryDigitalBillFeatureFlag, useQueryFailedPaymentPromptFeatureFlag } from '../hooks/useFeatureFlagApi';
import type { LocationInfoDto } from '@ordr-as/ordr-service-types/epos';
import { DeliveryMethodDtoType } from '@ordr-as/ordr-service-types/epos';

interface Props {
  location: LocationInfoDto;
}

const [LocationContextProvider, useLocation] = createContext(({ location }: Props) => {
  const { data: digitalBillFeatureFlag } = useQueryDigitalBillFeatureFlag(location.id);
  const { data: failedPaymentPromptFeatureFlag } = useQueryFailedPaymentPromptFeatureFlag(location.locationId);

  const isDigitalBillEnabled = Boolean(digitalBillFeatureFlag?.waiterCanInitiate);

  const formatCurrency = useCallback(
    (amount: number) => formatWithCurrency(amount, location.currency),
    [location.currency]
  );

  const formatCurrencyWithoutDecimals = useCallback(
    (amount: number) => formatWithCurrencyWithoutDecimals(amount, location.currency),
    [location.currency]
  );

  const formatAmountWithoutDecimals = useCallback(
    (amount: number) => formatWithoutCurrencyAndDecimals(amount, location.currency),
    [location.currency]
  );

  const counterDeliveryMethod = useMemo(
    () => location.deliveryMethods.find((d) => d.type === DeliveryMethodDtoType.COUNTER),
    [location.deliveryMethods]
  );

  const isTakeAwayPreselected = useMemo(
    () => location.deliveryMethods.some((dm) => dm.preSelected && dm.type === DeliveryMethodDtoType.TAKEAWAY),
    [location.deliveryMethods]
  );

  const featureFlags = useMemo(
    () => ({
      digitalBillFeatureFlag,
      failedPaymentPromptFeatureFlag,
    }),
    [digitalBillFeatureFlag, failedPaymentPromptFeatureFlag]
  );

  return useMemo(
    () => ({
      counterDeliveryMethod,
      featureFlags,
      formatAmountWithoutDecimals,
      formatCurrency,
      formatCurrencyWithoutDecimals,
      isDigitalBillEnabled,
      isTakeAwayPreselected,
      location,
    }),
    [
      location,
      formatAmountWithoutDecimals,
      formatCurrency,
      formatCurrencyWithoutDecimals,
      isDigitalBillEnabled,
      counterDeliveryMethod,
      isTakeAwayPreselected,
      featureFlags,
    ]
  );
});

export { LocationContextProvider, useLocation };
